import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { envelopeMessage, hasApiEnvelope } from '@/api/envelope';
import { showApiResultModal } from '@/lib/apiResultModal';

/** Optional Bearer token storage key — set after login when your API issues JWT. */
export const AUTH_TOKEN_KEY = 'gf_access_token';

export class ApiRequestError extends Error {
  status?: number;
  data?: unknown;

  constructor(message: string, status?: number, data?: unknown) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.data = data;
  }
}

declare module 'axios' {
  interface AxiosRequestConfig {
    /** Skip envelope check and modal for this request. */
    skipApiEnvelope?: boolean;
    /** When `error: false` and message is present, show success modal (default: false). */
    showSuccessModal?: boolean;
    /** Reject on `error: true` but do not show the global modal (page handles UI). */
    skipApiEnvelopeModal?: boolean;
  }
}

const rawBase = import.meta.env.VITE_API_BASE_URL as string | undefined;
const baseURL = rawBase?.replace(/\/$/, '') ?? '';

export const apiClient = axios.create({
  baseURL: baseURL || undefined,
  timeout: 30_000,
});

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.headers.language = 'en';
  return config;
});

apiClient.interceptors.response.use(
  (response) => {
    if (response.config.skipApiEnvelope) {
      return response;
    }

    const data = response.data;
    if (!hasApiEnvelope(data)) {
      return response;
    }

    const fallback = 'Request failed';
    const message = envelopeMessage(data, fallback);

    if (data.error === true) {
      if (!response.config.skipApiEnvelopeModal) {
        showApiResultModal({ message, success: false });
      }
      return Promise.reject(new ApiRequestError(message, response.status, data));
    }

    if (
      !response.config.skipApiEnvelopeModal &&
      response.config.showSuccessModal &&
      message !== fallback
    ) {
      showApiResultModal({ message, success: true });
    }

    return response;
  },
  (error: AxiosError) => Promise.reject(error),
);
